import { BinaryFileResult } from "@/types/generated/UsedGeneratedTypes";
import { Fetcher } from "../Fetcher";
import { EtagCache } from "../EtagCache";

export class FileService extends Fetcher {
  static INSTANCE: FileService = new FileService();

  /**
   * Cache ETag JS-side pour /filesOnRepository ( cf. pattern identique
   * dans DataService.getDataFilters ). 304 Not Modified instant après
   * 1er hit. Cache process-local , vidé au reload.
   *
   * Borne plus serrée que filterList car le payload peut grossir avec
   * le nombre de fichiers ( 200 fichiers × ~5 KB de params jsonb =
   * ~1 MB par entrée ) ; 20 entrées × ~1 MB = 20 MB max , match la
   * default heap browser.
   */
  static readonly #filesOnRepositoryEtagCache = new EtagCache<BinaryFileResult[]>({
    maxEntries: 20,
    maxBytes: 20 * 1024 * 1024,
  });

  constructor() {
    super();
  }

  async getFiles(applicationName: string, dataTypeId: string, selected: boolean): Promise<Array<BinaryFileResult>> {
    const url = `applications/${applicationName}/filesOnRepository/${dataTypeId}`;
    // selected porte typiquement les requiredAuthorizations ( un objet ) ; on
    // sérialise pour qu'il fasse partie de la clé de cache.
    const cacheKey = `${applicationName}::${dataTypeId}::${JSON.stringify(selected)}`;
    return this.getJsonWithEtag<BinaryFileResult[]>(
      url,
      { repositoryId: JSON.stringify(selected) },
      FileService.#filesOnRepositoryEtagCache,
      cacheKey,
    );
  }

  static clearFilesOnRepositoryEtagCache(): void {
    FileService.#filesOnRepositoryEtagCache.clear();
  }

  static getFilesOnRepositoryEtagCacheStats(): { entries: number; bytes: number; capacityEntries: number; capacityBytes: number } {
    return {
      entries: FileService.#filesOnRepositoryEtagCache.size(),
      bytes: FileService.#filesOnRepositoryEtagCache.totalBytes(),
      capacityEntries: FileService.#filesOnRepositoryEtagCache.capacity(),
      capacityBytes: FileService.#filesOnRepositoryEtagCache.byteCapacity(),
    };
  }

  async remove(applicationName: string, uuid: string): Promise<string> {
    // Toute mutation côté serveur invalide notre cache local pour ne pas
    // servir une liste obsolète après suppression.
    FileService.clearFilesOnRepositoryEtagCache();
    return this.delete(`applications/${applicationName}/file/${uuid}`);
  }

  /**
   * P0 cancel-divergence fix : interroge l'etat de publication courant
   * directement depuis {@code binaryfile.params} cote backend . Utilise
   * en post-terminal d'un workflow publish/unpublish/cancel pour avoir
   * la source de verite reelle , independemment du statut workflow_log
   * ( qui peut diverger en cas de race condition cancel/sweeper ) .
   *
   * <p>Pas de cache ETag - toujours frais ( endpoint backend leger ,
   * 1 SELECT binaryfile by PK + 1 lookup user ) .
   */
  async getPublicationState(
    applicationName: string,
    fileId: string,
  ): Promise<{ fileId: string; published: boolean; publishedOn: string | null; publishedByLogin: string | null }> {
    return this.get<{ fileId: string; published: boolean; publishedOn: string | null; publishedByLogin: string | null }>(
      `applications/${applicationName}/file/${fileId}/publication-state`,
    );
  }

  async download(applicationName: string, uuid: string): Promise<void> {
    return this.downloadFile(`applications/${applicationName}/file/${uuid}`);
  }
}
